import { Select, Spin } from 'antd';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';

import { vaccinationRegistryApi } from '@/api/vaccination/registry/api';
import { IndividualShort } from '@/api/vaccination/registry/types';
import { UUID } from '@/shared/api/types';
import { IndividualStatusLabel } from '@/shared/vaccination/enums';

const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_PAGE_SIZE = 20;

interface IndividualOption {
  label: string;
  value: UUID;
}

const toOption = (individual: IndividualShort): IndividualOption => ({
  label: `${individual.inventoryNumber} (${IndividualStatusLabel[individual.individualStatus] ?? individual.individualStatus})`,
  value: individual.id,
});

interface IIndividualSelectProps {
  value?: UUID;
  onChange?: (value?: UUID) => void;
  disabled?: boolean;
  organizationId?: UUID;
  placeholder?: string;
}

/**
 * Выбор особи из журнала ведения особей с поиском по инвентарному номеру на сервере.
 * Если хозяйство известно, поиск ограничивается им.
 */
export const IndividualSelect = ({
  value,
  onChange,
  disabled,
  organizationId,
  placeholder,
}: IIndividualSelectProps) => {
  const [searchIndividuals, { data, isFetching }] =
    vaccinationRegistryApi.endpoints.getRegistryRecords.useLazyQuery();
  const [selected, setSelected] = useState<IndividualOption | null>(null);

  const search = useMemo(
    () =>
      _.debounce((text: string) => {
        searchIndividuals(
          {
            filters: {
              inventoryNumber: text.trim() || undefined,
              organizationId,
            },
            paging: { page: 0, size: SEARCH_PAGE_SIZE },
            sort: [{ key: 'inventoryNumber', order: 'asc' }],
          },
          true,
        );
      }, SEARCH_DEBOUNCE_MS),
    [searchIndividuals, organizationId],
  );

  useEffect(() => () => search.cancel(), [search]);

  // Выбранная особь остаётся в списке, даже если её нет в результатах нового поиска
  const options = useMemo(() => {
    const found = data?.content?.map(toOption) ?? [];
    return selected && !found.some((option) => option.value === selected.value)
      ? [selected, ...found]
      : found;
  }, [data, selected]);

  return (
    <Select
      showSearch
      allowClear
      value={value}
      placeholder={placeholder}
      disabled={disabled}
      filterOption={false}
      options={options}
      loading={isFetching}
      notFoundContent={isFetching ? <Spin size="small" /> : 'Особи не найдены'}
      onFocus={() => {
        if (!data) search('');
      }}
      onSearch={search}
      onChange={(newValue?: UUID, option?: IndividualOption | IndividualOption[]) => {
        setSelected(option && !Array.isArray(option) ? option : null);
        onChange?.(newValue);
      }}
    />
  );
};
